import React, { useEffect, useState } from 'react';
import { isJwtExpired } from '../../utilities/token-utils';
import { ILxsServiceAgentViewProps } from './lxs-service-agent';

const INCOMING_ACTIVITY = 'DIRECT_LINE/INCOMING_ACTIVITY';

export default (props: ILxsServiceAgentViewProps) => {
    const { resources, CLASS_NAME } = props;
    const { isAuthenticated } = props.context.actionContext?.requestContext?.user;
    const [isOpen, setIsOpen] = useState<boolean>(false);
    const [isExpanded, setIsExpanded] = useState<boolean>(false);

    const webchatRef = React.useRef<HTMLDivElement | null>(null);

    useEffect(() => {
        if (typeof window === 'undefined') {
            return;
        }

        const directLineValue = props.directLineToken?.value;
        // wait until the token is present
        if (!directLineValue) {
            return;
        }

        const initWebChat = () => {
            const WebChat = (window as any).WebChat;
            const { token, firstName } = props.context.request.user;
            const { locale } = props.context.request;
            const tokenExpired = isJwtExpired(String(directLineValue));
            if (tokenExpired) {
                // clear any stored state we shouldn't use with a new/expired token
                sessionStorage.removeItem('conversationId');
                sessionStorage.removeItem('transcript');
            }

            // only try to restore conversationId when the token is valid
            const savedConversationId = tokenExpired ? undefined : sessionStorage.getItem('conversationId') || undefined;

            if (!WebChat || !webchatRef.current) {
                console.error('WebChat not available after load');
                return;
            }

            const directLine = WebChat.createDirectLine({
                token: directLineValue,
                conversationId: savedConversationId
            });

            const isRestoring = Boolean(savedConversationId);

            const store = WebChat.createStore(
                {},
                () => (next: (arg0: any) => any) => (action: { type: string; payload: { activity: any } }) => {
                    if (action.type === INCOMING_ACTIVITY) {
                        if (isRestoring) {
                            return next(action); // don't persist restored activities
                        }

                        const transcript = JSON.parse(sessionStorage.getItem('transcript') || '[]');
                        transcript.push(action.payload.activity);

                        // keep transcript bounded to avoid quota issues
                        const MAX_ACTIVITIES = 200;
                        const sliced = transcript.slice(-MAX_ACTIVITIES);

                        try {
                            sessionStorage.setItem('transcript', JSON.stringify(sliced));
                        } catch (e) {
                            console.error('Failed to save transcript', e);
                        }
                    }

                    return next(action);
                }
            );
            const savedTranscript = JSON.parse(sessionStorage.getItem('transcript') || '[]');
            savedTranscript.forEach((activity: any) => {
                store.dispatch({
                    type: INCOMING_ACTIVITY,
                    payload: { activity }
                });
            });

            const connSub = directLine.connectionStatus$.subscribe((status: number) => {
                if (status === 2) {
                    const conversationId = directLine.conversationId;
                    sessionStorage.setItem('conversationId', conversationId);
                    directLine
                        .postActivity({
                            from: { id: firstName },
                            type: 'event',
                            name: 'pvaSetContext',
                            value: { jwtLoopNeu: token, name: firstName, locale }
                        })
                        .subscribe(
                            (_: string) => {
                                if (!isRestoring) {
                                    directLine
                                        .postActivity({
                                            from: { id: firstName },
                                            type: 'event',
                                            name: 'startConversationPortal'
                                        })
                                        .subscribe((err2: any) => console.error('startConversation postActivity error', err2));
                                }
                            },
                            (err1: any) => console.error('pvaSetContext postActivity error', err1)
                        );
                    connSub.unsubscribe();
                }
            });

            WebChat.renderWebChat(
                {
                    directLine,
                    userID: firstName,
                    locale,
                    store,
                    styleOptions: { hideUploadButton: true }
                },
                webchatRef.current
            );
        };

        initWebChat();
    }, [props.directLineToken?.value, props.context?.request?.user]);

    return (
        <>
            {isAuthenticated && (
                <>
                    <div className={CLASS_NAME}>
                        <button
                            className={`${CLASS_NAME}__button`}
                            onClick={() => {
                                setIsOpen(!isOpen);
                            }}
                        >
                            {resources.lxsServiceAgentName}
                        </button>
                    </div>
                    <div
                        className={`${CLASS_NAME}__frame ${isOpen ? `${CLASS_NAME}__frame--open` : `${CLASS_NAME}__frame--closed`} ${
                            !isExpanded ? '' : `${CLASS_NAME}__frame--expanded`
                        }`}
                    >
                        <div className={`${CLASS_NAME}__frame-heading`}>
                            <h4 className={`${CLASS_NAME}__frame-heading-text`}>{resources.lxsServiceAgentTitle}</h4>
                            <button
                                className={`${CLASS_NAME}__frame-${isExpanded ? 'minimize' : 'expand'}`}
                                onClick={() => {
                                    setIsExpanded(!isExpanded);
                                }}
                            ></button>
                        </div>
                        <div id={CLASS_NAME} ref={webchatRef} />
                    </div>
                </>
            )}
        </>
    );
};
